/**
 * 에피소드 결함 태그 축의 다중 선택 선택지를 계산한다(순수 계산).
 *
 * 서버는 한 축 안의 값, 갈래, 존재 토큰(`any`/`none`)을 모두 **합집합(OR)** 으로 받는다. 그래서
 * 화면도 세 가지를 한 드롭다운에 함께 둔다. 컨트롤을 나누면 사용자가 둘 사이를 AND 로 이해한다.
 * 데이터셋 목록의 `에피소드 결함` 과 에피소드 목록의 `결함` 이 이 빌더를 함께 쓴다.
 *
 * 순서는 다음과 같다:
 * 1. `[있음][없음]`
 * 2. 갈래마다 `[갈래 전체]` 와 그 갈래의 값
 * 3. 어느 갈래에도 속하지 않는 값
 *
 * 갈래 제목은 선택할 수 없는 그룹 머리다. 그 아래 전체 선택과 개별 값을 둔다. 검색해도
 * 소속 제목을 유지하고, 갈래 이름으로 검색하면 그 갈래의 모든 값이 남는다.
 *
 * 갈래 행의 값은 `category:<code>` 형식이다. 값 어휘와 한 선택 배열에 섞이므로 접두사로
 * 구분한다. 서버로 보낼 때 `splitTagSelection` 이 두 파라미터로 나눈다.
 */

import { EPISODE_TAG_ANY, EPISODE_TAG_NONE } from "@/api/datasetEpisode";

const CATEGORY_PREFIX = "category:";

/** c6 다중 선택 옵션과 같은 모양이지만 이 모듈은 c6 에 의존하지 않는다. `count` 가 없으면 배지를 그리지 않는다. */
export type EpisodeTagChoice = {
  value: string;
  label: string;
  description?: string;
  count?: number;
  disabled?: boolean;
  group?: { value: string; label: string };
  included?: boolean;
};

/** 존재 토큰 한 칸의 문구다(`결함 있음` / `결함 없음`). */
export type EpisodeTagTokenChoice = {
  label: string;
  description: string;
  count?: number;
};

export type EpisodeTagCategoryChoice = {
  code: string;
  displayName: string;
  count?: number;
};

export type EpisodeTagChoiceInput = {
  // 값 어휘. 표시 순서대로 둔다. 수를 모르면 count 를 뺀다.
  tags: readonly { value: string; count?: number }[];
  // 값 표시 문구. 예) `episodeQualityTagText`
  tagText: (tag: string) => string;
  // 소속을 모를 때 값 아래에 표시할 설명이다. 묶인 값은 제목 아래 한 줄로 표시한다.
  tagTip?: (tag: string) => string | null;
  any: EpisodeTagTokenChoice;
  none: EpisodeTagTokenChoice;
  // 갈래. 표시 순서대로 둔다. 갈래가 없으면 비운다.
  categories?: readonly EpisodeTagCategoryChoice[];
  // 갈래 code → 소속 값. 모르면(label-categories 실패) null 이다. 그때는 갈래 행은 표시하고 값은
  // 소속 없이 평면으로 표시한다. 서버가 갈래 선택을 값으로 풀기 때문에 소속을 몰라도 고를 수 있다.
  membership?: ReadonlyMap<string, readonly string[]> | null;
};

/**
 * 한 축의 선택지 목록. 배타 잠금(`disabled`)은 여기서 적용하지 않는다. 선택 상태에 따라 바뀌는
 * 표시 규칙이라 `lockExclusiveTagChoices` 가 드롭다운 직전에 추가한다.
 */
export function episodeTagChoices(input: EpisodeTagChoiceInput): readonly EpisodeTagChoice[] {
  const choices: EpisodeTagChoice[] = [
    tokenChoice(EPISODE_TAG_ANY, input.any),
    tokenChoice(EPISODE_TAG_NONE, input.none),
  ];
  const categories = input.categories ?? [];
  const membership = input.membership ?? null;

  if (membership === null) {
    for (const category of categories) {
      choices.push(categoryChoice(category));
    }
    for (const tag of input.tags) {
      choices.push(tagChoice(tag, input));
    }
    return choices;
  }

  const placed = new Set<string>();
  for (const category of categories) {
    const members = new Set(membership.get(category.code) ?? []);
    // 값 어휘와 겹치는 소속 값이 없는 갈래는 표시하지 않는다. 같은 key 의 다른 축(데이터셋 결함)의
    // 갈래이므로, 이 축으로 고르면 서버가 422 로 거절한다.
    const memberTags = input.tags.filter((tag) => members.has(tag.value) && !placed.has(tag.value));
    if (memberTags.length === 0) {
      continue;
    }
    choices.push(categoryChoice(category));
    for (const tag of memberTags) {
      placed.add(tag.value);
      choices.push({
        ...tagChoice(tag, { tagText: input.tagText }),
        group: categoryGroup(category),
      });
    }
  }
  // 갈래로 묶인 값이 있으면 나머지는 "기타 결함" 제목 아래에 둔다.
  const grouped = placed.size > 0;
  for (const tag of input.tags) {
    if (placed.has(tag.value)) {
      continue;
    }
    const choice = tagChoice(tag, input);
    choices.push(
      grouped ? { ...choice, group: { value: "ungrouped", label: "기타 결함" } } : choice,
    );
  }
  return choices;
}

/**
 * 존재 토큰과 나머지 선택지를 서로 잠근다.
 * - `있음` 을 고르면 값·갈래를 더해도 결과가 같다.
 * - `없음` 에 값을 더하면 "없거나 그 값" 이라는 쓸모없는 조합이 된다.
 * 토큰 하나를 고르면 다른 칸 전부를 잠근다. 값·갈래를 하나라도 고르면 토큰 두 개를 잠근다
 * (Training `취소 제외` 와 같은 규약).
 *
 * 드롭다운 옵션 prop 에만 추가한다. 필터 그룹(카탈로그)에 disabled 를 넣으면 c6 정규화가 이미
 * 선택된 값을 알림 없이 제거한다.
 */
export function lockExclusiveTagChoices(
  choices: readonly EpisodeTagChoice[],
  selected: readonly string[],
): readonly EpisodeTagChoice[] {
  const token = selected.find(isTagToken);
  if (token !== undefined) {
    return choices.map((choice) =>
      choice.value === token ? choice : { ...choice, disabled: true },
    );
  }
  if (selected.length === 0) {
    return choices;
  }
  return choices.map((choice) =>
    isTagToken(choice.value)
      ? { ...choice, disabled: true }
      : choice.group && selected.includes(choice.group.value) && choice.value !== choice.group.value
        ? { ...choice, included: true, description: "갈래 전체에 포함됨" }
        : choice,
  );
}

/** 갈래를 고르면 그 안의 개별 값은 빼서 쿼리와 칩에 중복 저장하지 않는다. */
export function normalizeTagSelection(
  choices: readonly EpisodeTagChoice[],
  selected: readonly string[],
): readonly string[] {
  const token = selected.find(isTagToken);
  if (token !== undefined) return [token];
  return selected.filter((value) => {
    const group = choices.find((choice) => choice.value === value)?.group;
    return !group || value === group.value || !selected.includes(group.value);
  });
}

/** 갈래 행의 선택 값. */
export function categoryChoiceValue(code: string): string {
  return CATEGORY_PREFIX + code;
}

/** 선택 값이 갈래 행인지 판정한다. */
export function isCategoryChoiceValue(value: string): boolean {
  return value.startsWith(CATEGORY_PREFIX);
}

/**
 * 선택 배열을 서버 파라미터 두 개로 나눈다. 값과 토큰은 `tags`, 갈래는 `categories`(code)다.
 *
 * 예) `["none"]` → `{tags: ["none"], categories: []}`,
 * `["category:termination", "collision"]` → `{tags: ["collision"], categories: ["termination"]}`.
 */
export function splitTagSelection(values: readonly string[]): {
  tags: string[];
  categories: string[];
} {
  const tags: string[] = [];
  const categories: string[] = [];
  for (const value of values) {
    if (isCategoryChoiceValue(value)) {
      categories.push(value.slice(CATEGORY_PREFIX.length));
    } else {
      tags.push(value);
    }
  }
  return { tags, categories };
}

/** `splitTagSelection` 의 역연산. 서버 파라미터 두 개를 드롭다운 선택 배열로 합친다. */
export function joinTagSelection(
  tags: readonly string[],
  categories: readonly string[],
): readonly string[] {
  return [...tags, ...categories.map(categoryChoiceValue)];
}

/**
 * 갈래 문구 `한글(code)`. 옆 값 행이 `한글(영어)` 형식이므로 표기를 맞춰야 한다. code 는 목록
 * 질의 토큰이라 MCP·API 응답과 비교할 때 기준이 된다. 이름이 code 와 같으면 괄호를 쓰지 않는다.
 */
export function tagCategoryText(category: { code: string; displayName: string }): string {
  return category.displayName === category.code
    ? category.code
    : `${category.displayName}(${category.code})`;
}

function isTagToken(value: string): boolean {
  return value === EPISODE_TAG_ANY || value === EPISODE_TAG_NONE;
}

function tokenChoice(value: string, token: EpisodeTagTokenChoice): EpisodeTagChoice {
  return withCount(
    {
      value,
      label: token.label,
      description: token.description,
      group: { value: "record", label: "기록 여부" },
    },
    token.count,
  );
}

function categoryChoice(category: EpisodeTagCategoryChoice): EpisodeTagChoice {
  return withCount(
    {
      value: categoryChoiceValue(category.code),
      label: `${tagCategoryText(category)} 전체`,
      description: "이 갈래 전체 · 검색 결과와 무관하게 선택",
      group: categoryGroup(category),
    },
    category.count,
  );
}

function categoryGroup(category: EpisodeTagCategoryChoice): { value: string; label: string } {
  return { value: categoryChoiceValue(category.code), label: category.displayName };
}

function tagChoice(
  tag: { value: string; count?: number },
  input: Pick<EpisodeTagChoiceInput, "tagText" | "tagTip">,
): EpisodeTagChoice {
  const tip = input.tagTip?.(tag.value) ?? null;
  return withCount(
    {
      value: tag.value,
      label: input.tagText(tag.value),
      group: { value: "individual", label: "개별 결함" },
      ...(tip === null ? {} : { description: tip }),
    },
    tag.count,
  );
}

// count 가 없으면 키 자체를 빼서 c6 가 배지를 그리지 않게 한다. c6 는 `0` 도 배지로 그린다.
function withCount(choice: EpisodeTagChoice, count: number | undefined): EpisodeTagChoice {
  return count === undefined ? choice : { ...choice, count };
}
